"use client";

import type { AgentThreadShareVo } from "@/api";
import {
  CopyIcon,
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
} from "@/assets/icons";
import CopiedSuccessIcon from "@/assets/icons/copied-success.svg";
import ModalCloseIcon from "@/assets/icons/modal-close.svg";
import RemixIcon from "@/assets/icons/remix.svg";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { useToast } from "@/component/Toast/useToast";
import { VideoPlayer } from "@/component/VideoPlayer";
import { useShareLink } from "@/hooks/useShareLink";
import { useSharedThread } from "@/hooks/useSharedThread";
import { type UrlAttachment } from "@/types/attachment";
import { type ChatDraft } from "@/types/draft-strorage";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { attachmentsToPersisted } from "@/util/draftConverter";
import { getDraftKey, setDraft } from "@/util/draftStorage";
import { getThreadSharePath } from "@/util/share";
import "@ant-design/v5-patch-for-react-19";
import { Button, ConfigProvider, Modal, Skeleton } from "antd";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import SimpleBar from "simplebar-react";
import OpenIcon from "./assets/open-icon.svg";
import styles from "./index.module.css";
interface SharedThreadModalProps {
  open: boolean;
  threadId: string;
  onClose: () => void;
}

interface SocialIconButtonProps {
  className: string;
  onClick: () => void;
  icon: ReactNode;
}

const SocialIconButton = ({
  onClick,
  icon,
  className,
}: SocialIconButtonProps) => (
  <Button className={className} onClick={onClick}>
    {icon}
  </Button>
);

const SkeletonContent = () => {
  return (
    <div className="flex flex-col gap-4 max-w-[1600px] lg:flex-row justify-center">
      <div className="w-full flex">
        <Skeleton.Node
          active
          className="block! w-full! h-auto! aspect-video! rounded-2xl!"
        />
      </div>

      <aside className="w-full lg:w-[340px] shrink-0 pt-0 flex flex-col gap-5">
        <Skeleton.Input active style={{ height: 32 }} />
        <Skeleton active paragraph={{ rows: 6 }} title={false} />
      </aside>
    </div>
  );
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div className="text-sm text-black/80 font-bold">{children}</div>
);

interface ThreadSectionProps {
  className?: string;
  title: string;
  children: ReactNode;
}

const ThreadSection = ({ title, children, className }: ThreadSectionProps) => (
  <section className={classNames("mt-5", className)}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </section>
);

const ThreadVideo = ({ src }: { src?: string }) => {
  return (
    <div className="flex-1 box-content">
      <div className="w-full h-full flex justify-center overflow-hidden">
        {src ? (
          <VideoPlayer
            src={src}
            className="w-full items-start"
            style={{ borderRadius: "16px", overflow: "hidden" }}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            Video not found
          </div>
        )}
      </div>
    </div>
  );
};

interface ThreadAsideProps {
  thread: AgentThreadShareVo;
  onRemix: () => void;
}

const ThreadAside = ({ thread, onRemix }: ThreadAsideProps) => {
  const shareLink = thread.id ? getThreadSharePath(thread.id) : "";

  const [isCopied, setIsCopied] = useState(false);

  const { shareOnTwitter, shareOnFacebook, shareOnLinkedin, handleCopyLink } =
    useShareLink({
      shareLink,
    });

  const onCopyLinkClick = () => {
    const success = handleCopyLink();
    if (success) {
      setIsCopied(true);
      logger.event(VidMuseEvent.ThreadSharePageCopyLinkBtnClick, {
        thread_id: thread.id,
      });
    }
  };

  useEffect(() => {
    if (!isCopied) return;
    const timer = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [isCopied]);

  const shareActions = [
    {
      key: "twitter",
      onClick: () => {
        shareOnTwitter();
        logger.event(VidMuseEvent.ThreadSharePageShareSocialBtnClick, {
          thread_id: thread.id,
          type: "twitter",
        });
      },
      icon: <TwitterIcon className="w-4 h-4" />,
    },
    {
      key: "facebook",
      onClick: () => {
        shareOnFacebook();
        logger.event(VidMuseEvent.ThreadSharePageShareSocialBtnClick, {
          thread_id: thread.id,
          type: "facebook",
        });
      },
      icon: <FacebookIcon className="w-4 h-4" />,
    },
    {
      key: "linkedin",
      onClick: () => {
        shareOnLinkedin();
        logger.event(VidMuseEvent.ThreadSharePageShareSocialBtnClick, {
          thread_id: thread.id,
          type: "linkedin",
        });
      },
      icon: <LinkedinIcon className="w-4 h-4" />,
    },
  ];

  const skillTags: string[] = [];

  return (
    <aside className="flex flex-col w-full lg:w-[340px] shrink-0 pt-0">
      <h2 className="text-xl leading-normal font-bold">{thread.title}</h2>

      <ThreadSection title="Prompt" className="flex flex-col flex-1 min-h-0">
        <SimpleBar className="flex-1 min-h-0 bg-[rgba(0,0,0,0.03)] rounded-xl p-4 mt-[10px]">
          <p className="text-sm text-[#4D596C] whitespace-pre-wrap">
            {thread.prompt}
          </p>
        </SimpleBar>
      </ThreadSection>

      {skillTags.length > 0 ? (
        <ThreadSection title="Skill tags">
          <div className="mt-[10px] flex flex-wrap gap-3">
            {skillTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center h-9 px-3 rounded-full bg-[rgba(0,0,0,0.03)] text-[#4d596c] text-xs font-normal"
              >
                {tag}
              </span>
            ))}
          </div>
        </ThreadSection>
      ) : null}

      <ThreadSection title="Share">
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorText: "#4D596C",
                controlHeight: 36,
                fontWeight: 500,
                fontSize: 12,
                paddingInline: 20,
                colorBgContainer: "rgba(0, 0, 0, 0.04)",
                borderRadius: 40,
                defaultShadow: "none",
              },
            },
          }}
        >
          <div className="mt-[10px] flex items-center gap-3">
            {shareActions.map(({ key, onClick, icon }) => (
              <SocialIconButton
                className="flex-1"
                key={key}
                onClick={onClick}
                icon={icon}
              />
            ))}

            {isCopied ? (
              <div className="flex-5 gap-1! h-9 text-sm flex items-center justify-center text-[#4D596C] bg-[rgba(0,0,0,0.04)] rounded-[40px] font-medium">
                Link copied <CopiedSuccessIcon className="w-4 h-4" />
              </div>
            ) : (
              <Button
                onClick={onCopyLinkClick}
                className="flex-5 gap-1!"
                icon={<CopyIcon className="w-4 h-4" />}
              >
                Copy Link
              </Button>
            )}
          </div>
        </ConfigProvider>
      </ThreadSection>

      <div className="mt-5 flex items-center gap-4">
        <ConfigProvider
          theme={{
            components: {
              Button: {
                fontSize: 14,
                fontWeight: 500,
                colorText: "#4D596C",
                borderRadius: 44,
                controlHeight: 40,
              },
            },
          }}
        >
          <Button
            type="primary"
            onClick={onRemix}
            className="flex-1 p-0!"
            icon={<RemixIcon className="w-4 h-4" />}
          >
            Remix
          </Button>
          <Link href={`/thread/${thread.id}`} className="block flex-1">
            <Button
              type="text"
              onClick={() =>
                logger.event(VidMuseEvent.ThreadSharePageOpenProject, {
                  thread_id: thread.id,
                })
              }
              className="w-full text-[#4D596C]! border-[rgba(152,162,179,0.30)]!"
              icon={<OpenIcon className="w-4 h-4" />}
            >
              Open project
            </Button>
          </Link>
        </ConfigProvider>
      </div>
    </aside>
  );
};

/**
 * 通过 threadId 获取完整的线程详情并展示
 */
export function SharedThreadModal(props: SharedThreadModalProps) {
  const { open, threadId, onClose } = props;
  const router = useRouter();
  const { error: showErrorToast } = useToast();

  // 根据 threadId 获取完整的线程详情
  const { thread: displayThread, isLoading } = useSharedThread(threadId);

  const shouldShowSkeleton = !displayThread && isLoading;

  const handleRemix = async () => {
    if (!displayThread) return;

    try {
      // 1. 从 thread.files 中读取 filePath，转换为 URL 附件
      const attachments: UrlAttachment[] = (displayThread.files || [])
        .filter((file) => file.filePath) // 过滤掉没有 filePath 的文件
        .map((file, index) => {
          const url = convertFilePathToUrl(file.filePath);
          return {
            type: "url" as const,
            id: `remix-${index}-${Date.now()}`,
            url,
            preview: url, // 对于 URL 附件，preview 也使用 URL
          };
        })
        .filter((attachment) => attachment.url); // 过滤掉转换失败的 URL

      // 2. 将附件转换为可持久化的格式
      const persistedAttachments = await attachmentsToPersisted(attachments);

      // 3. 创建草稿数据
      const draft: ChatDraft = {
        text: displayThread.prompt || "",
        isAuto: true,
        attachments: persistedAttachments,
        updatedAt: Date.now(),
      };

      // 4. 保存草稿到 IndexedDB
      await setDraft(getDraftKey(), draft);

      // 5. 跳转到首页
      router.push("/");

      // 6. 关闭弹窗
      onClose();

      // log
      logger.event(VidMuseEvent.ThreadSharePageRemix, {
        thread_id: displayThread.id,
      });
    } catch (error) {
      console.error("Failed to remix thread:", error);
      showErrorToast("Failed to remix project. Please try again.");
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            controlHeightLG: 40,
            defaultBorderColor: "transparent",
          },
        },
      }}
    >
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        maskClosable={false}
        closeIcon={null}
        rootClassName={styles.modalRoot}
        classNames={{
          body: "flex justify-center",
          mask: styles.modalMask,
          content: styles.modalContent,
        }}
      >
        <div className="w-full">
          <div className="relative h-[60px] flex items-center justify-between px-5  mx-auto max-w-[1600px]">
            <Link href="/">
              <div className="flex items-center gap-2">
                <Image src="/logo.png" alt="VidMuse" width={24} height={24} />
                <span className="text-xl font-bold text-black h-[30px] leading-[30px]">
                  VidMuse
                </span>
              </div>
            </Link>

            <button
              type="button"
              className="cursor-pointer bg-transparent border-0 p-0"
              onClick={onClose}
              aria-label="Close thread showcase modal"
            >
              <ModalCloseIcon className="w-5 h-5 text-[#98A2B3]" />
            </button>
          </div>

          <div className="relative mt-5 px-5 lg:mt-20 mx-auto w-full max-w-[1600px]">
            {shouldShowSkeleton ? (
              <SkeletonContent />
            ) : (
              displayThread && (
                <div className="flex flex-col gap-5 items-stretch lg:flex-row">
                  <ThreadVideo
                    src={convertFilePathToUrl(displayThread.resultPath)}
                  />
                  <ThreadAside thread={displayThread} onRemix={handleRemix} />
                </div>
              )
            )}
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
}
